/**
 * Orders View Component - Extracted from CRMTab
 * Handles order listing, filtering, CRUD operations
 */
import React from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Card, CardContent } from '../ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Plus,
  Search,
  Pencil,
  Trash2,
  Download,
  RefreshCw,
  Upload,
  Receipt,
} from 'lucide-react';
import { SortableTableHeader } from './SortableTableHeader';
import { Pagination } from './Pagination';
import { ORDER_STATUSES, PAYMENT_STATUSES, STATUS_COLORS, PAYMENT_COLORS } from './crmConstants';
import { getSensitiveTopicLabel } from '../../utils/sensitiveTopicsTranslations';

export const OrdersView = ({
  orders,
  pagination,
  search,
  setSearch,
  statusFilter,
  setStatusFilter,
  paymentFilter,
  setPaymentFilter,
  sortField,
  sortOrder,
  handleSort,
  handleSearch,
  fetchOrders,
  openOrderDialog,
  handleDeleteOrder,
  handleStatusChange,
  handleUpdateOrderStatus,
  viewOrderExpense,
  handleExportOrders,
  setOrderImportDialogOpen,
  language,
  t,
}) => {
  return (
    <>
      {/* Filters */}
      <Card className="p-4">
        <form onSubmit={handleSearch} className="flex flex-wrap gap-3">
          <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder={t.search}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>
          <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v === 'all' ? '' : v); }}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder={t.allStatuses} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t.allStatuses}</SelectItem>
              {ORDER_STATUSES.map(s => (
                <SelectItem key={s} value={s}>{s}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={paymentFilter} onValueChange={(v) => { setPaymentFilter(v === 'all' ? '' : v); }}>
            <SelectTrigger className="w-[150px]">
              <SelectValue placeholder={t.allPayments} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t.allPayments}</SelectItem>
              {PAYMENT_STATUSES.map(s => (
                <SelectItem key={s} value={s}>{s}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button type="submit"><Search className="h-4 w-4" /></Button>
          <Button 
            type="button" 
            variant="outline" 
            onClick={() => { setSearch(''); setStatusFilter(''); setPaymentFilter(''); fetchOrders(1); }} 
            className="hover:bg-slate-100"
          >
            <RefreshCw className="h-4 w-4" />
          </Button>
        </form>
      </Card>

      {/* Actions */}
      <div className="flex justify-between items-center">
        <p className="text-slate-500 font-medium">
          {pagination.total} {t.orders}
        </p>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleExportOrders} className="border-emerald-300 text-emerald-700 hover:bg-emerald-50 hover:border-emerald-400">
            <Download className="h-4 w-4 mr-2" />
            {t.export}
          </Button>
          <Button onClick={() => openOrderDialog()} className="bg-indigo-600 hover:bg-indigo-700 text-white shadow-md">
            <Plus className="h-4 w-4 mr-2" />
            {t.newOrder}
          </Button>
          <Button onClick={() => setOrderImportDialogOpen(true)} variant="outline" className="border-indigo-300 text-indigo-600 hover:bg-indigo-50">
            <Upload className="h-4 w-4 mr-2" />
            {language === 'es' ? 'Importar Pedidos' : 'Import Orders'}
          </Button>
        </div>
      </div>

      {/* Orders Table */}
      <Card className="shadow-md border-slate-200">
        <CardContent className="p-0 overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <SortableTableHeader field="order_number" sortField={sortField} sortOrder={sortOrder} onSort={handleSort}>
                  {t.orderNumber}
                </SortableTableHeader>
                <TableHead>{t.externalOrderNumber}</TableHead>
                <SortableTableHeader field="customer_name" sortField={sortField} sortOrder={sortOrder} onSort={handleSort}>
                  {t.customer}
                </SortableTableHeader>
                <TableHead>{t.productUrl}</TableHead>
                <TableHead>{t.status}</TableHead>
                <TableHead>{t.paymentStatus}</TableHead>
                <SortableTableHeader field="pvp" sortField={sortField} sortOrder={sortOrder} onSort={handleSort}>
                  {t.pvp}
                </SortableTableHeader>
                <TableHead>{t.writer}</TableHead>
                <SortableTableHeader field="order_date" sortField={sortField} sortOrder={sortOrder} onSort={handleSort}>
                  {t.orderDate}
                </SortableTableHeader>
                <TableHead className="text-right">{t.actions}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {orders.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10} className="text-center py-8 text-muted-foreground">
                    {t.noOrders}
                  </TableCell>
                </TableRow>
              ) : (
                orders.map((order) => (
                  <TableRow key={order.id} className="hover:bg-slate-50/50">
                    <TableCell className="font-bold text-indigo-600">#{order.order_number}</TableCell>
                    <TableCell className="text-muted-foreground">{order.external_order_number || '-'}</TableCell>
                    <TableCell>
                      <div>
                        <div className="font-medium">{order.customer_name}</div>
                        <div className="text-xs text-muted-foreground">{order.customer_email}</div>
                      </div>
                    </TableCell>
                    <TableCell className="max-w-[240px]">
                      <div className="truncate">{order.product_url}</div>
                      {(order.product_link_type !== undefined || order.product_accepts_sensitive !== undefined || order.product_ad_label !== undefined) && (
                        <div className="flex flex-wrap gap-1 mt-1" data-testid={`product-info-${order.order_number}`}>
                          {order.product_link_type ? (
                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-100 text-blue-700">
                              {order.product_link_type}
                            </span>
                          ) : null}
                          {order.product_accepts_sensitive ? (
                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-orange-100 text-orange-700">
                              {language === 'es' ? 'Sensible: Sí' : 'Sensitive: Yes'}
                            </span>
                          ) : (
                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-600">
                              {language === 'es' ? 'Sensible: No' : 'Sensitive: No'}
                            </span>
                          )}
                          {order.product_ad_label ? (
                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-yellow-100 text-yellow-800" title={order.product_ad_label}>
                              {language === 'es' ? 'Etiqueta: Sí' : 'Tag: Yes'}
                            </span>
                          ) : (
                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-600">
                              {language === 'es' ? 'Etiqueta: No' : 'Tag: No'}
                            </span>
                          )}
                          {Array.isArray(order.product_sensitive_topics) && order.product_sensitive_topics.length > 0 && (
                            order.product_sensitive_topics.map((tk) => (
                              <span key={tk} className="text-[10px] px-1.5 py-0.5 rounded bg-rose-100 text-rose-700">
                                {getSensitiveTopicLabel(tk, language)}
                              </span>
                            ))
                          )}
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      <Select 
                        value={order.status} 
                        onValueChange={(v) => handleStatusChange(order.id, v)}
                      >
                        <SelectTrigger className={`w-[140px] ${STATUS_COLORS[order.status]}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {ORDER_STATUSES.map(s => (
                            <SelectItem key={s} value={s}>{s}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Select 
                          value={order.payment_status} 
                          onValueChange={(v) => handleUpdateOrderStatus(order.id, 'payment_status', v)}
                        >
                          <SelectTrigger className={`w-[120px] h-8 text-xs ${PAYMENT_COLORS[order.payment_status]}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {PAYMENT_STATUSES.map(s => (
                              <SelectItem key={s} value={s}>{s}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {viewOrderExpense && (
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => viewOrderExpense(order.id)}
                            title={order.payment_status === 'Pagado' ? t.viewExpense : (t.addExpense || 'Añadir gasto')}
                            className="h-7 w-7 p-0 text-primary hover:text-primary/80"
                            data-testid={`view-expense-${order.order_number}`}
                          >
                            <Receipt className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>€{order.pvp}</TableCell>
                    <TableCell>{order.writer}</TableCell>
                    <TableCell>{order.order_date ? new Date(order.order_date).toLocaleDateString() : '-'}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button variant="ghost" size="sm" onClick={() => openOrderDialog(order)} className="text-slate-600 hover:text-indigo-600 hover:bg-indigo-50">
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDeleteOrder(order.id)} className="text-slate-400 hover:text-rose-600 hover:bg-rose-50">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Pagination */}
      {pagination.total_pages > 1 && (
        <Pagination
          pagination={pagination}
          onPageChange={(page) => fetchOrders(page)}
          language={language}
        />
      )}
    </>
  );
};

export default OrdersView;
